import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Chip,
  Alert,
  Paper,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  AccessTime as TimeIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { FormattedMessage } from "react-intl";
import { PaymentHistoryItem } from "../../../redux/types/types";
import { getPaymentStripeDetailsApi } from "../../../utils/api/failedPayments";

interface FailedPayment extends PaymentHistoryItem {
  stripeSessionId?: string;
  stripePaymentIntentId?: string;
  responseCode?: string;
  responseMessage?: string;
  webhookLogs?: Array<{
    id: string;
    status: string;
    responseCode: string;
    responseMessage: string;
    createdAt: string;
    signatureValid: boolean;
  }>;
}

interface PaymentFailureDetailsProps {
  payment: FailedPayment;
  formatAmount: (amount: number) => string;
  formatDateTime: (dateString: string) => string;
}

// Stripe failure code explanations
const STRIPE_FAILURE_EXPLANATIONS: Record<
  string,
  {
    category: string;
    userFriendly: string;
    technical: string;
    actionRequired: string;
  }
> = {
  // Card declined errors
  card_declined: {
    category: "Card Declined",
    userFriendly: "The customer's bank declined the payment",
    technical: "Generic decline - the bank didn't provide specific details",
    actionRequired:
      "Ask customer to contact their bank or try a different payment method",
  },
  insufficient_funds: {
    category: "Card Declined",
    userFriendly: "Insufficient funds in the customer's account",
    technical: "The bank declined due to insufficient funds",
    actionRequired:
      "Customer needs to add funds to their account or use a different card",
  },
  expired_card: {
    category: "Card Issue",
    userFriendly: "The customer's card has expired",
    technical: "Card expiry date has passed",
    actionRequired: "Customer needs to update their card information",
  },
  incorrect_cvc: {
    category: "Card Issue",
    userFriendly: "Incorrect security code (CVC)",
    technical: "The CVC provided doesn't match the card",
    actionRequired: "Customer should re-enter the correct CVC",
  },
  incorrect_number: {
    category: "Card Issue",
    userFriendly: "Incorrect card number",
    technical: "The card number provided is invalid",
    actionRequired: "Customer should re-enter the correct card number",
  },
  incorrect_zip: {
    category: "Card Issue",
    userFriendly: "Incorrect billing postal code",
    technical: "The postal code doesn't match the card",
    actionRequired: "Customer should verify and correct their billing address",
  },
  // Processing errors
  processing_error: {
    category: "Processing Error",
    userFriendly: "A temporary processing error occurred",
    technical: "Stripe or the bank had a temporary processing issue",
    actionRequired: "Try again later - this is usually temporary",
  },
  // Authentication errors
  authentication_required: {
    category: "Authentication",
    userFriendly: "3D Secure authentication was required but not completed",
    technical: "Strong Customer Authentication (SCA) failed or was abandoned",
    actionRequired: "Customer needs to complete 3D Secure authentication",
  },
  // Risk-related
  stolen_card: {
    category: "Security",
    userFriendly: "The card was reported as stolen",
    technical: "Card is flagged as stolen in bank systems",
    actionRequired:
      "Customer needs to contact their bank or use a different card",
  },
  lost_card: {
    category: "Security",
    userFriendly: "The card was reported as lost",
    technical: "Card is flagged as lost in bank systems",
    actionRequired:
      "Customer needs to contact their bank or use a different card",
  },
  // Rate limiting
  card_velocity_exceeded: {
    category: "Rate Limiting",
    userFriendly: "Too many transactions attempted in a short period",
    technical: "Card exceeded velocity limits",
    actionRequired: "Wait before trying again or use a different card",
  },
};

const getFailureSeverity = (code: string): "error" | "warning" | "info" => {
  if (["stolen_card", "lost_card", "authentication_required"].includes(code)) {
    return "error";
  }
  if (["processing_error", "card_velocity_exceeded"].includes(code)) {
    return "warning";
  }
  return "info";
};

export const PaymentFailureDetails: React.FC<PaymentFailureDetailsProps> = ({
  payment,
  formatAmount,
  formatDateTime,
}) => {
  const [stripeDetails, setStripeDetails] = useState<any>(null);
  const [loadingStripeDetails, setLoadingStripeDetails] = useState(false);
  const [stripeDetailsError, setStripeDetailsError] = useState<string | null>(
    null
  );

  const {
    responseCode,
    responseMessage,
    webhookLogs = [],
    stripeSessionId,
    stripePaymentIntentId,
  } = payment;

  const fetchStripeDetails = useCallback(async () => {
    if (!stripeSessionId) return;

    setLoadingStripeDetails(true);
    setStripeDetailsError(null);

    try {
      const response = await getPaymentStripeDetailsApi(payment.id);
      setStripeDetails(response.data);
    } catch (error: any) {
      console.error("Failed to fetch Stripe details:", error);
      setStripeDetailsError(
        error.response?.data?.message ||
          "Failed to fetch detailed payment information"
      );
    } finally {
      setLoadingStripeDetails(false);
    }
  }, [payment.id, stripeSessionId]);

  useEffect(() => {
    // Auto-fetch Stripe details when component mounts
    fetchStripeDetails();
  }, [fetchStripeDetails]);

  const mainFailure = responseCode
    ? STRIPE_FAILURE_EXPLANATIONS[responseCode]
    : null;
  const severity = responseCode ? getFailureSeverity(responseCode) : "info";

  return (
    <Box>
      {/* Basic Payment Information */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography
              variant="subtitle1"
              gutterBottom
              color="primary"
              fontWeight="bold"
            >
              <FormattedMessage
                id="admin.failedPayments.basicInfo"
                defaultMessage="Basic Information"
              />
            </Typography>
            <Grid container spacing={10}>
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    <FormattedMessage
                      id="admin.failedPayments.id"
                      defaultMessage="ID:"
                    />
                  </Typography>
                  <Typography
                    variant="body2"
                    fontFamily="monospace"
                    fontSize="0.85rem"
                  >
                    {payment.id}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    <FormattedMessage
                      id="admin.failedPayments.amount"
                      defaultMessage="Amount:"
                    />
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatAmount(payment.amount)} {payment.currency}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    <FormattedMessage
                      id="admin.failedPayments.status"
                      defaultMessage="Status:"
                    />
                  </Typography>
                  <Chip
                    label={payment.status}
                    color={payment.status === "FAILED" ? "error" : "warning"}
                    size="small"
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    <FormattedMessage
                      id="admin.failedPayments.created"
                      defaultMessage="Created:"
                    />
                  </Typography>
                  <Typography variant="body2">
                    {formatDateTime(payment.createdAt)}
                  </Typography>
                </Box>
              </Grid>

              {payment.paidAt && payment.paidAt !== null && (
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      <FormattedMessage
                        id="admin.failedPayments.paid"
                        defaultMessage="Paid:"
                      />
                    </Typography>
                    <Typography variant="body2">
                      {formatDateTime(payment.paidAt)}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Main Failure Information */}
      {responseCode && (
        <Alert
          severity={severity}
          sx={{ mb: 2 }}
          icon={
            severity === "error" ? (
              <ErrorIcon />
            ) : severity === "warning" ? (
              <WarningIcon />
            ) : (
              <InfoIcon />
            )
          }
        >
          <Typography variant="subtitle2" fontWeight="bold">
            {mainFailure?.category || "Payment Failure"}
          </Typography>
          <Typography variant="body2">
            {mainFailure?.userFriendly ||
              responseMessage ||
              "Payment failed without specific details"}
          </Typography>

          {mainFailure && (
            <Box sx={{ mt: 1 }}>
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
              >
                <strong>Technical:</strong> {mainFailure.technical}
              </Typography>
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
              >
                <strong>Action:</strong> {mainFailure.actionRequired}
              </Typography>
            </Box>
          )}
        </Alert>
      )}

      {/* Technical Details */}
      <Accordion defaultExpanded={false}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            <FormattedMessage id="admin.failedPayments.technicalDetails" />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            {stripeSessionId && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Stripe Session ID
                  </Typography>
                  <Typography
                    variant="body2"
                    fontFamily="monospace"
                    fontSize="0.85rem"
                    sx={{
                      wordBreak: "break-all",
                      backgroundColor: "grey.100",
                      p: 1,
                      borderRadius: 1,
                      mt: 0.5,
                    }}
                  >
                    {stripeSessionId}
                  </Typography>
                </Paper>
              </Grid>
            )}

            {stripePaymentIntentId && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Payment Intent ID
                  </Typography>
                  <Typography
                    variant="body2"
                    fontFamily="monospace"
                    fontSize="0.85rem"
                    sx={{
                      wordBreak: "break-all",
                      backgroundColor: "grey.100",
                      p: 1,
                      borderRadius: 1,
                      mt: 0.5,
                    }}
                  >
                    {stripePaymentIntentId}
                  </Typography>
                </Paper>
              </Grid>
            )}

            {(responseCode || responseMessage) && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Error Details
                  </Typography>
                  {responseCode && (
                    <Chip
                      label={`Code: ${responseCode}`}
                      color="error"
                      size="small"
                      sx={{ mt: 1, mr: 1 }}
                    />
                  )}
                  {responseMessage && (
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 1,
                        fontStyle: "italic",
                        color: "error.main",
                      }}
                    >
                      "{responseMessage}"
                    </Typography>
                  )}
                </Paper>
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Detailed Stripe Information */}
      {stripeSessionId && (
        <Accordion defaultExpanded={false} sx={{ mt: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              <FormattedMessage
                id="admin.failedPayments.stripeDetails"
                defaultMessage="Stripe Details"
              />
            </Typography>
            {loadingStripeDetails && (
              <CircularProgress size={20} sx={{ ml: 1 }} />
            )}
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={(e) => {
                e.stopPropagation();
                fetchStripeDetails();
              }}
              disabled={loadingStripeDetails}
              sx={{ ml: "auto", mr: 1 }}
            >
              <FormattedMessage
                id="admin.failedPayments.refresh"
                defaultMessage="Refresh"
              />
            </Button>
          </AccordionSummary>
          <AccordionDetails>
            {stripeDetailsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {stripeDetailsError}
              </Alert>
            )}

            {stripeDetails && (
              <Grid container spacing={2}>
                {/* Session Details */}
                {stripeDetails.stripeDetails.session && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2 }}>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        color="primary"
                      >
                        Session Information
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Status:
                          </Typography>
                          <Chip
                            label={stripeDetails.stripeDetails.session.status}
                            size="small"
                            color={
                              stripeDetails.stripeDetails.session.status ===
                              "expired"
                                ? "error"
                                : "default"
                            }
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Payment Status:
                          </Typography>
                          <Chip
                            label={
                              stripeDetails.stripeDetails.session.payment_status
                            }
                            size="small"
                            color={
                              stripeDetails.stripeDetails.session
                                .payment_status === "unpaid"
                                ? "error"
                                : "success"
                            }
                          />
                        </Grid>
                        {stripeDetails.stripeDetails.session.customer_email && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              Customer Email:
                            </Typography>
                            <Typography variant="body2">
                              {
                                stripeDetails.stripeDetails.session
                                  .customer_email
                              }
                            </Typography>
                          </Grid>
                        )}
                        {stripeDetails.stripeDetails.session.expires_at && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              Expires At:
                            </Typography>
                            <Typography variant="body2">
                              {new Date(
                                stripeDetails.stripeDetails.session.expires_at *
                                  1000
                              ).toLocaleString()}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Paper>
                  </Grid>
                )}

                {/* Payment Intent Details */}
                {stripeDetails.stripeDetails.payment_intent && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2 }}>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        color="primary"
                      >
                        Payment Intent
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Status:
                          </Typography>
                          <Chip
                            label={
                              stripeDetails.stripeDetails.payment_intent.status
                            }
                            size="small"
                            color={
                              stripeDetails.stripeDetails.payment_intent
                                .status === "failed"
                                ? "error"
                                : "default"
                            }
                          />
                        </Grid>
                        {stripeDetails.stripeDetails.payment_intent
                          .last_payment_error && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              Last Error:
                            </Typography>
                            <Alert severity="error" sx={{ mt: 1 }}>
                              <Typography variant="body2">
                                <strong>Code:</strong>{" "}
                                {
                                  stripeDetails.stripeDetails.payment_intent
                                    .last_payment_error.code
                                }
                              </Typography>
                              <Typography variant="body2">
                                <strong>Message:</strong>{" "}
                                {
                                  stripeDetails.stripeDetails.payment_intent
                                    .last_payment_error.message
                                }
                              </Typography>
                              {stripeDetails.stripeDetails.payment_intent
                                .last_payment_error.decline_code && (
                                <Typography variant="body2">
                                  <strong>Decline Code:</strong>{" "}
                                  {
                                    stripeDetails.stripeDetails.payment_intent
                                      .last_payment_error.decline_code
                                  }
                                </Typography>
                              )}
                            </Alert>
                          </Grid>
                        )}
                      </Grid>
                    </Paper>
                  </Grid>
                )}

                {/* Latest Charge Details */}
                {stripeDetails.stripeDetails.latest_charge && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2 }}>
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        color="primary"
                      >
                        Latest Charge
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Status:
                          </Typography>
                          <Chip
                            label={
                              stripeDetails.stripeDetails.latest_charge.status
                            }
                            size="small"
                            color={
                              stripeDetails.stripeDetails.latest_charge
                                .status === "failed"
                                ? "error"
                                : "default"
                            }
                          />
                        </Grid>
                        {stripeDetails.stripeDetails.latest_charge
                          .failure_code && (
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Failure Code:
                            </Typography>
                            <Chip
                              label={
                                stripeDetails.stripeDetails.latest_charge
                                  .failure_code
                              }
                              size="small"
                              color="error"
                            />
                          </Grid>
                        )}
                        {stripeDetails.stripeDetails.latest_charge
                          .failure_message && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              Failure Message:
                            </Typography>
                            <Typography
                              variant="body2"
                              color="error.main"
                              sx={{ fontStyle: "italic" }}
                            >
                              {
                                stripeDetails.stripeDetails.latest_charge
                                  .failure_message
                              }
                            </Typography>
                          </Grid>
                        )}
                        {stripeDetails.stripeDetails.latest_charge.outcome && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              Outcome:
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                              <Chip
                                label={`Type: ${stripeDetails.stripeDetails.latest_charge.outcome.type}`}
                                size="small"
                                sx={{ mr: 1 }}
                              />
                              {stripeDetails.stripeDetails.latest_charge.outcome
                                .network_status && (
                                <Chip
                                  label={`Network: ${stripeDetails.stripeDetails.latest_charge.outcome.network_status}`}
                                  size="small"
                                  sx={{ mr: 1 }}
                                />
                              )}
                              {stripeDetails.stripeDetails.latest_charge.outcome
                                .reason && (
                                <Chip
                                  label={`Reason: ${stripeDetails.stripeDetails.latest_charge.outcome.reason}`}
                                  size="small"
                                  color="error"
                                />
                              )}
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Webhook Event Timeline */}
      {webhookLogs.length > 0 && (
        <Accordion defaultExpanded={false} sx={{ mt: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              <FormattedMessage id="admin.failedPayments.eventTimeline" />
            </Typography>
            <Chip
              label={`${webhookLogs.length} events`}
              size="small"
              sx={{ ml: 1 }}
            />
          </AccordionSummary>
          <AccordionDetails>
            <List disablePadding>
              {webhookLogs.map((log, index) => (
                <ListItem
                  key={log.id}
                  sx={{ px: 0, pb: 2, alignItems: "flex-start" }}
                >
                  <ListItemIcon>
                    <TimeIcon
                      color={
                        log.status.includes("failed") ||
                        log.status.includes("error")
                          ? "error"
                          : log.status.includes("success") ||
                            log.status.includes("completed")
                          ? "success"
                          : "primary"
                      }
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Typography variant="subtitle2">
                            {log.status
                              .replace(/[._]/g, " ")
                              .replace(/\b\w/g, (l) => l.toUpperCase())}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDateTime(log.createdAt)}
                          </Typography>
                        </Box>

                        {log.responseMessage && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                          >
                            {log.responseMessage}
                          </Typography>
                        )}

                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          {log.responseCode && (
                            <Chip
                              label={`Code: ${log.responseCode}`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                          <Chip
                            label={
                              log.signatureValid
                                ? "Verified"
                                : "Invalid Signature"
                            }
                            size="small"
                            color={log.signatureValid ? "success" : "error"}
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};
